import {useEffect} from 'react';
import {
    formatTime,
    handleLoadedMetadata,
    handleTimeUpdate,
    handleVideoEnd,
    handleVideoReset
} from '@dazn/lp-web/src/utils/videoBannerUtils';

interface UseVideoControlsProps {
    videoRef: React.RefObject<HTMLVideoElement>;
    isLoaded: boolean;
    source: string;
    imgSource: string;
    currentPlayedtime: number;
    setCurrentTime: (time: string) => void;
    setTotalDuration: (time: string) => void;
    setTotalMilliseconds: (ms: number) => void;
    setIsPlaying: (isPlaying: boolean) => void;
    toggleFullScreen: () => void;
    togglePlayPause: () => void;
    handleBackward10Seconds: () => void;
    handleForward10Seconds: () => void;
    handleVolumeUp: () => void;
    handleVolumeDown: () => void;
    toggleMuteUnmute: () => void;
    setIsFullScreen: (fullScreen: boolean) => void;
}

const useVideoControls = ({
    videoRef,
    isLoaded,
    source,
    imgSource,
    currentPlayedtime,
    setCurrentTime,
    setTotalDuration,
    setTotalMilliseconds,
    setIsPlaying,
    toggleFullScreen,
    togglePlayPause,
    setIsFullScreen,
    handleBackward10Seconds,
    handleForward10Seconds,
    handleVolumeUp,
    handleVolumeDown,
    toggleMuteUnmute
}: UseVideoControlsProps) => {
    useEffect(() => {
        const video = videoRef.current;

        if (video) {
            const hasCurrentTime =
                currentPlayedtime !== null && currentPlayedtime > 0;
            video.muted = true;
            const playbackTime = hasCurrentTime ? currentPlayedtime : 0;
            if (video.src !== source) {
                video.src = source; // Update the source (this will not cause a reload if it's the same source)
                if (video.poster !== imgSource) {
                    video.poster = imgSource;
                }

                if (!isLoaded) {
                    // Load the video only if it's not already loaded
                    video.load();
                }
            }
            // Resume playback from the preserved or default time
            video.currentTime = playbackTime;
            video
                .play()
                .then(() => {
                    video.muted = false;
                })
                .catch((error) => {
                    new Error(error);
                });
            setTotalDuration(formatTime(video.duration));
            setCurrentTime(formatTime(video.currentTime));
        }
    }, [isLoaded, videoRef, source, setTotalDuration, setCurrentTime]);

    useEffect(() => {
        const videoElement = videoRef.current;

        if (videoElement) {
            const handleTimeUpdateEvent = () =>
                handleTimeUpdate(videoElement, setCurrentTime);
            const handleLoadedMetadataEvent = () =>
                handleLoadedMetadata(
                    videoElement,
                    setTotalDuration,
                    setTotalMilliseconds
                );
            const handleVideoEndEvent = () => {
                handleVideoEnd(setIsPlaying);
                handleVideoReset(videoElement);
            };

            videoElement.addEventListener('timeupdate', handleTimeUpdateEvent);
            videoElement.addEventListener(
                'loadedmetadata',
                handleLoadedMetadataEvent
            );
            videoElement.addEventListener('ended', handleVideoEndEvent);

            return () => {
                videoElement.removeEventListener(
                    'timeupdate',
                    handleTimeUpdateEvent
                );
                videoElement.removeEventListener(
                    'loadedmetadata',
                    handleLoadedMetadataEvent
                );
                videoElement.removeEventListener('ended', handleVideoEndEvent);
            };
        }
    }, [videoRef, setCurrentTime, setTotalDuration, setTotalMilliseconds]);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            video.removeAttribute('controls');

            const handleMouseEnter = () => {
                video.controls = false;
            };
            const handleMouseLeave = () => {
                video.controls = false;
            };

            video.addEventListener('mouseenter', handleMouseEnter);
            video.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                video.removeEventListener('mouseenter', handleMouseEnter);
                video.removeEventListener('mouseleave', handleMouseLeave);
            };
        }
    }, [videoRef]);

    useEffect(() => {
        const keyActions: Record<string, () => void> = {
            Escape: () => {
                if (document.fullscreenElement) toggleFullScreen();
            },
            f: toggleFullScreen,
            F: toggleFullScreen,
            ' ': togglePlayPause,
            ArrowLeft: handleBackward10Seconds,
            ArrowRight: handleForward10Seconds,
            ArrowUp: handleVolumeUp,
            ArrowDown: handleVolumeDown,
            m: toggleMuteUnmute,
            M: toggleMuteUnmute
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const action = keyActions[event.key];
            if (action) {
                event.preventDefault();
                event.stopImmediatePropagation();
                action();
            }
        };

        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) setIsFullScreen(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange
            );
        };
    }, [
        toggleFullScreen,
        togglePlayPause,
        handleBackward10Seconds,
        handleForward10Seconds,
        handleVolumeUp,
        handleVolumeDown,
        toggleMuteUnmute,
        setIsFullScreen
    ]);
};

export default useVideoControls;
